import { useCallback, useMemo, useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  IconButton,
  Image,
  Input,
  Select,
  SimpleGrid,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Text,
  useColorModeValue,
  useToast,
  VStack,
  Badge,
  Divider,
  Tooltip,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCrop,
  faImage,
  faTrash,
  faUpload,
  faFolder,
  faVideo,
  faFileLines,
  faVolumeHigh,
  faRotateRight,
  faMagnifyingGlassPlus,
  faCompress,
  faCheck,
  faTimes,
  faSun,
  faMoon,
  faArrowsUpDownLeftRight,
  faArrowUp,
  faArrowDown,
  faArrowLeft,
  faArrowRight,
  faCrosshairs,
  faSliders,
} from "@fortawesome/free-solid-svg-icons";
import Cropper from "react-easy-crop";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import {
  useApp,
  useMedia,
  useSelectionReference,
  useStructureDirty,
  useS3Client,
  useContentStructure,
} from "@/hooks/contextHooks";
import { apiGetRequest, apiPutRequest } from "@/functions/requests";
import { MediaMeta, MediaType } from "@/pages/mediaManager";
import { GetObjectCommand, PutObjectCommand, S3Client } from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { updateContentItemInTree } from "@/functions/editorFunctions";
import { NewSection } from "@xnav/types/src/common";
import { ReactStateType } from "@xnav/types/src/frontend";

export default function SectionEditor() {
  const [selectionReference, setSelectionReference] = useSelectionReference() as any;
  const [media, setMedia] = useMedia() as any;
  const [sectionMeta, setSectionMeta] = useContentStructure() as ReactStateType<NewSection[] | undefined>;
  const app = useApp() as any;
  const [, setStructureDirty] = useStructureDirty();
  const s3 = useS3Client() as S3Client;
  const toast = useToast();

  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [aspectPreset, setAspectPreset] = useState<number>(360 / 88); // Default Mobile Card Option A (approx 4.09:1)
  const [quality, setQuality] = useState<number>(0.85); // Compression ratio
  const [previewMode, setPreviewMode] = useState<"dark" | "light">("dark");
  const [isCropping, setIsCropping] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [croppedPreviewUrl, setCroppedPreviewUrl] = useState<string | null>(null);
  const [showMediaPicker, setShowMediaPicker] = useState<boolean>(false);
  const [iconSignedUrl, setIconSignedUrl] = useState<string | null>(null);

  const bgPanel = useColorModeValue("white", "gray.850");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const bgContainer = useColorModeValue("gray.50", "gray.900");
  const bgPicker = useColorModeValue("gray.50", "gray.800");
  const bgCropper = useColorModeValue("gray.100", "gray.900");
  const bgControlCard = useColorModeValue("white", "gray.800");

  const onCropComplete = useCallback((_croppedArea: any, pixels: any) => {
    setCroppedAreaPixels(pixels);
  }, []);

  // Locate active section icon if already assigned
  const activeMediaIcon = useMemo(() => {
    if (!selectionReference?.iconId || !media) return null;
    return media.find((m: MediaMeta) => m.id === selectionReference.iconId || m.iconId === selectionReference.iconId);
  }, [selectionReference?.iconId, media]);

  // Load signed icon URL if section has an iconId not readily in media list
  useEffect(() => {
    let isSubscribed = true;
    async function loadIcon() {
      if (!selectionReference?.iconId || !app?.id || !s3) {
        if (isSubscribed) setIconSignedUrl(null);
        return;
      }
      const found = media?.find(
        (m: MediaMeta) => m.id === selectionReference.iconId || m.iconId === selectionReference.iconId
      );
      const candidateUrl = found?.iconUrl || found?.fileUrl;
      if (candidateUrl && candidateUrl.startsWith("http")) {
        if (isSubscribed) setIconSignedUrl(candidateUrl);
        return;
      }
      try {
        const getMediaIconCmd = new GetObjectCommand({
          Bucket: import.meta.env.VITE_EDIT_BUCKET_NAME,
          Key: `${app.id}/media/icons/${selectionReference.iconId}`,
        });
        const url = await getSignedUrl(s3 as any, getMediaIconCmd as any, { expiresIn: 3600 });
        if (isSubscribed) setIconSignedUrl(url);
      } catch (e) {
        console.warn("Could not get signed URL for icon:", e);
      }
    }
    loadIcon();
    return () => {
      isSubscribed = false;
    };
  }, [selectionReference?.iconId, media, app?.id, s3]);

  // Count contents
  const mediaCounts = useMemo(() => {
    const counts = { section: 0, video: 0, pdf: 0, audio: 0 };
    if (!selectionReference?.contents) return counts;
    const countItems = (items: any[]) => {
      items.forEach((item) => {
        if (item.type === "section") counts.section++;
        else if (item.type === "video") counts.video++;
        else if (item.type === "pdf") counts.pdf++;
        else if (item.type === "audio") counts.audio++;
        if (item.contents && Array.isArray(item.contents)) {
          countItems(item.contents);
        }
      });
    };
    countItems(selectionReference.contents);
    return counts;
  }, [selectionReference?.contents]);

  // Reset when selection changes
  useEffect(() => {
    setIsCropping(false);
    setImageSrc(null);
    setCroppedPreviewUrl(null);
    setShowMediaPicker(false);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
  }, [selectionReference?.id]);

  if (!selectionReference || selectionReference.type !== "section") {
    return <></>;
  }

  // Convert remote URL or S3 key to clean blob URL to prevent canvas cross-origin tainting
  const getImageCleanBlobUrl = async (
    rawUrl?: string | null,
    iconId?: string | null,
    mediaId?: string | null
  ): Promise<string | null> => {
    if (rawUrl && (rawUrl.startsWith("data:") || rawUrl.startsWith("blob:"))) {
      return rawUrl;
    }

    // Attempt 1: Fetch via authenticated S3Client if we have an iconId or mediaId
    if (s3 && app?.id) {
      const candidates: string[] = [];
      if (iconId) candidates.push(`${app.id}/media/icons/${iconId}`);
      if (mediaId) candidates.push(`${app.id}/media/media/${mediaId}`);

      for (const key of candidates) {
        try {
          const getCmd = new GetObjectCommand({
            Bucket: import.meta.env.VITE_EDIT_BUCKET_NAME,
            Key: key,
          });
          const res = await s3.send(getCmd);
          if (res.Body) {
            const bytes = await (res.Body as any).transformToByteArray();
            const blob = new Blob([bytes], { type: res.ContentType || "image/jpeg" });
            return URL.createObjectURL(blob);
          }
        } catch (s3Err) {
          console.warn(`[SectionEditor] S3 direct fetch failed for key ${key}:`, s3Err);
        }
      }
    }

    // Attempt 2: Fetch the URL via fetch(url) with CORS
    if (rawUrl) {
      try {
        const response = await fetch(rawUrl, { mode: "cors" });
        if (response.ok) {
          const blob = await response.blob();
          return URL.createObjectURL(blob);
        }
      } catch (fetchErr) {
        console.warn("[SectionEditor] fetch(rawUrl) failed:", fetchErr);
      }
    }

    return rawUrl || null;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setImageSrc(reader.result?.toString() || null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setRotation(0);
        setIsCropping(true);
        setShowMediaPicker(false);
      });
      reader.readAsDataURL(file);
    }
  };

  const createCroppedCanvas = async (): Promise<{ blob: Blob; previewUrl: string } | null> => {
    if (!imageSrc || !croppedAreaPixels) return null;
    const image = new window.Image();
    image.crossOrigin = "anonymous";
    image.src = imageSrc;

    if (!image.complete) {
      await new Promise((resolve, reject) => {
        image.onload = resolve;
        image.onerror = () => reject(new Error("Failed to load source image for cropping."));
      });
    }

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Could not initialize 2D canvas context.");

    const width = Math.max(1, Math.round(croppedAreaPixels.width));
    const height = Math.max(1, Math.round(croppedAreaPixels.height));

    canvas.width = width;
    canvas.height = height;

    // Apply rotation if needed
    if (rotation !== 0) {
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.translate(-canvas.width / 2, -canvas.height / 2);
    }

    ctx.drawImage(
      image,
      Math.round(croppedAreaPixels.x),
      Math.round(croppedAreaPixels.y),
      width,
      height,
      0,
      0,
      width,
      height
    );

    return new Promise((resolve, reject) => {
      try {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Canvas conversion to blob returned empty."));
              return;
            }
            const previewUrl = URL.createObjectURL(blob);
            resolve({ blob, previewUrl });
          },
          "image/jpeg",
          quality
        );
      } catch (err: any) {
        reject(new Error(err?.message || "Failed to export cropped image from canvas."));
      }
    });
  };

  const handleApplyCropAndUpload = async () => {
    try {
      setIsUploading(true);
      const cropped = await createCroppedCanvas();
      if (!cropped) throw new Error("Could not process cropped image.");

      const newIconId = `${uuidv4()}.jpg`;
      const arrayBuffer = await cropped.blob.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);

      let uploadSuccess = false;
      let lastErrorMessage = "";

      // Method A: Direct S3 PutObject via S3Client (bypasses API Gateway presigned URL signature issues)
      if (s3 && app?.id) {
        try {
          const putIconCmd = new PutObjectCommand({
            Bucket: import.meta.env.VITE_EDIT_BUCKET_NAME,
            Key: `${app.id}/media/icons/${newIconId}`,
            Body: uint8Array,
            ContentType: "image/jpeg",
          });
          const putMediaCmd = new PutObjectCommand({
            Bucket: import.meta.env.VITE_EDIT_BUCKET_NAME,
            Key: `${app.id}/media/media/${newIconId}`,
            Body: uint8Array,
            ContentType: "image/jpeg",
          });
          await Promise.all([s3.send(putIconCmd), s3.send(putMediaCmd)]);
          uploadSuccess = true;
          console.log("[SectionEditor] Direct S3 upload succeeded for", newIconId);
        } catch (s3Err: any) {
          console.warn("[SectionEditor] Direct S3 upload failed, trying presigned fallback:", s3Err);
          lastErrorMessage = s3Err?.message || String(s3Err);
        }
      }

      // Method B: Presigned URL fallback
      if (!uploadSuccess && app?.id) {
        try {
          const signedUrl = await apiPutRequest<undefined, string>(
            `/mediaIcon/${app.id}/${newIconId}`,
            undefined
          );
          if (signedUrl) {
            await axios.put(signedUrl, cropped.blob, {
              headers: { "Content-Type": "image/jpeg" },
            });
            uploadSuccess = true;
            console.log("[SectionEditor] Presigned URL upload succeeded for", newIconId);
          }
        } catch (apiErr: any) {
          console.warn("[SectionEditor] Presigned URL upload failed:", apiErr);
          lastErrorMessage = apiErr?.response?.data?.message || apiErr?.message || lastErrorMessage;
        }
      }

      if (!uploadSuccess) {
        throw new Error(lastErrorMessage || "Failed to upload chapter banner to storage.");
      }

      let s3SignedUrl: string = cropped.previewUrl;
      try {
        if (s3 && app?.id) {
          const getCmd = new GetObjectCommand({
            Bucket: import.meta.env.VITE_EDIT_BUCKET_NAME,
            Key: `${app.id}/media/icons/${newIconId}`,
          });
          s3SignedUrl = await getSignedUrl(s3 as any, getCmd as any, { expiresIn: 3600 });
        }
      } catch (e) {
        console.warn("Could not generate signed URL immediately, using preview blob", e);
      }

      // Create new MediaMeta record
      const newMediaItem: MediaMeta = {
        id: newIconId,
        iconId: newIconId,
        type: MediaType.Image,
        name: `${selectionReference.title || "Section"} Banner`,
        fileUrl: s3SignedUrl,
        iconUrl: s3SignedUrl,
        references: [selectionReference.id],
        uploaded: new Date(),
        size: cropped.blob.size,
      };

      setMedia((prev: MediaMeta[]) => [...(prev || []), newMediaItem]);
      setSelectionReference({
        ...selectionReference,
        iconId: newIconId,
      });
      if (setSectionMeta) {
        setSectionMeta((prev) =>
          prev ? updateContentItemInTree(prev, selectionReference.id, { iconId: newIconId }) : prev
        );
      }
      setIconSignedUrl(s3SignedUrl);
      setStructureDirty(true);
      setCroppedPreviewUrl(s3SignedUrl);
      setIsCropping(false);

      toast({
        title: "Chapter Banner Positioned & Saved",
        description: "Exact framing baked in and synchronized for student mobile app! Click 'Save Changes' on top bar to persist.",
        status: "success",
        duration: 3500,
        isClosable: true,
      });
    } catch (err: any) {
      console.error("Error cropping/uploading banner:", err);
      toast({
        title: "Upload Failed",
        description: err?.message || "Failed to upload chapter banner.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleSelectExistingMedia = async (mediaItem: MediaMeta) => {
    const url = mediaItem.fileUrl || mediaItem.iconUrl;
    try {
      const cleanUrl = await getImageCleanBlobUrl(
        url,
        mediaItem.iconId || mediaItem.id,
        mediaItem.id
      );
      if (!cleanUrl) throw new Error("Could not load media image.");
      setImageSrc(cleanUrl);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setIsCropping(true);
      setShowMediaPicker(false);
      toast({
        title: "Position Chapter Banner",
        description: "Fine-tune banner framing & position for mobile devices below, then save.",
        status: "info",
        duration: 3500,
        isClosable: true,
      });
    } catch (e: any) {
      console.error("Error loading media for positioning:", e);
      setSelectionReference({
        ...selectionReference,
        iconId: mediaItem.id,
      });
      if (setSectionMeta) {
        setSectionMeta((prev) =>
          prev ? updateContentItemInTree(prev, selectionReference.id, { iconId: mediaItem.id }) : prev
        );
      }
      setStructureDirty(true);
      setShowMediaPicker(false);
    }
  };

  const handleStartAdjustCurrentBanner = async () => {
    if (!activeDisplayImageUrl && !selectionReference?.iconId) return;
    try {
      const cleanUrl = await getImageCleanBlobUrl(
        activeDisplayImageUrl,
        selectionReference?.iconId,
        null
      );
      if (!cleanUrl) throw new Error("Could not load current banner image.");
      setImageSrc(cleanUrl);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setIsCropping(true);
      setShowMediaPicker(false);
      toast({
        title: "Adjusting Banner Positioning",
        description: "Shift the ultrasound scan or medical diagram to highlight the exact target area.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
    } catch (err: any) {
      console.error("Error setting image for positioning:", err);
      toast({
        title: "Error Loading Banner",
        description: err?.message || "Could not load banner for positioning.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleRemoveBanner = () => {
    setSelectionReference({
      ...selectionReference,
      iconId: undefined,
    });
    if (setSectionMeta) {
      setSectionMeta((prev) =>
        prev ? updateContentItemInTree(prev, selectionReference.id, { iconId: undefined }) : prev
      );
    }
    setStructureDirty(true);
    setImageSrc(null);
    setCroppedPreviewUrl(null);
    setIconSignedUrl(null);
    setIsCropping(false);
    toast({
      title: "Banner Removed",
      description: "Chapter will display default clean dark card on mobile.",
      status: "info",
      duration: 2500,
      isClosable: true,
    });
  };

  const activeDisplayImageUrl =
    croppedPreviewUrl ||
    (isCropping && imageSrc) ||
    iconSignedUrl ||
    activeMediaIcon?.fileUrl ||
    activeMediaIcon?.iconUrl ||
    null;

  return (
    <Flex flex={1} h="100%" flexDir="column" overflowY="auto" p={6} bg={bgContainer}>
      <Card bg={bgPanel} border="1px solid" borderColor={borderColor} borderRadius="2xl" p={6} shadow="sm">
        {/* Header */}
        <Flex justify="space-between" align="center" mb={6} pb={4} borderBottom="1px solid" borderColor={borderColor}>
          <HStack spacing={3}>
            <Box
              p={3}
              borderRadius="xl"
              bgGradient="linear(to-br, cyan.500/15, teal.500/15)"
              border="1px solid"
              borderColor="cyan.500/30"
              color="cyan.400"
            >
              <FontAwesomeIcon icon={faImage} size="lg" />
            </Box>
            <VStack align="start" spacing={0.5}>
              <HStack spacing={2}>
                <Heading size="md">{selectionReference.title || "Untitled Chapter"}</Heading>
                <Badge
                  bgGradient="linear(to-r, #06b6d4, #10b981)"
                  color="gray.950"
                  fontWeight="bold"
                  letterSpacing="wider"
                  borderRadius="full"
                  px={2.5}
                  py={0.5}
                  fontSize="10px"
                  textTransform="uppercase"
                >
                  Option A Banner
                </Badge>
              </HStack>
              <Text fontSize="xs" color="gray.500">
                Chapter banner card styling & mobile live preview studio
              </Text>
            </VStack>
          </HStack>

          <HStack spacing={2.5}>
            <Button
              size="sm"
              bgGradient="linear(to-r, #06b6d4, #10b981)"
              color="white"
              fontWeight="600"
              borderRadius="xl"
              px={4}
              leftIcon={<FontAwesomeIcon icon={faUpload} />}
              _hover={{
                bgGradient: "linear(to-r, #0891b2, #059669)",
                boxShadow: "0 4px 14px rgba(6, 182, 212, 0.35)",
                transform: "translateY(-1px)",
              }}
              _active={{ transform: "translateY(0)" }}
              transition="all 0.15s ease"
              as="label"
              cursor="pointer"
            >
              Upload Scan
              <input type="file" accept="image/*" onChange={handleFileSelect} style={{ display: "none" }} />
            </Button>
            <Button
              size="sm"
              variant="outline"
              borderColor={useColorModeValue("gray.300", "whiteAlpha.300")}
              color={useColorModeValue("gray.700", "gray.100")}
              bg={useColorModeValue("white", "whiteAlpha.50")}
              fontWeight="600"
              borderRadius="xl"
              px={4}
              leftIcon={<FontAwesomeIcon icon={faImage} color="#00e5ff" />}
              _hover={{
                borderColor: "teal.400",
                color: "teal.300",
                bg: useColorModeValue("teal.50", "whiteAlpha.100"),
                boxShadow: "0 2px 10px rgba(20, 184, 166, 0.2)",
              }}
              transition="all 0.15s ease"
              onClick={() => {
                setShowMediaPicker(!showMediaPicker);
                setIsCropping(false);
              }}
            >
              Media Library
            </Button>
            {activeDisplayImageUrl && (
              <Tooltip label="Shift image upward, downward, or zoom to frame ultrasound features" hasArrow>
                <Button
                  size="sm"
                  variant="outline"
                  borderColor="cyan.500/40"
                  color="cyan.400"
                  bg="cyan.500/10"
                  fontWeight="600"
                  borderRadius="xl"
                  px={4}
                  leftIcon={<FontAwesomeIcon icon={faArrowsUpDownLeftRight} />}
                  _hover={{
                    borderColor: "teal.400",
                    color: "teal.300",
                    bg: "teal.500/20",
                    boxShadow: "0 2px 12px rgba(6, 182, 212, 0.25)",
                    transform: "translateY(-1px)",
                  }}
                  _active={{ transform: "translateY(0)" }}
                  transition="all 0.15s ease"
                  onClick={handleStartAdjustCurrentBanner}
                >
                  Adjust Position
                </Button>
              </Tooltip>
            )}
            {selectionReference.iconId && (
              <Button
                size="sm"
                variant="outline"
                borderColor="red.500/30"
                color="red.400"
                bg="red.500/10"
                fontWeight="600"
                borderRadius="xl"
                px={3.5}
                leftIcon={<FontAwesomeIcon icon={faTrash} />}
                _hover={{
                  bg: "red.500/20",
                  borderColor: "red.400",
                  color: "red.300",
                  boxShadow: "0 2px 10px rgba(239, 68, 68, 0.25)",
                }}
                transition="all 0.15s ease"
                onClick={handleRemoveBanner}
              >
                Remove
              </Button>
            )}
            <IconButton
              aria-label="Close Section Editor"
              icon={<FontAwesomeIcon icon={faTimes} />}
              size="sm"
              variant="ghost"
              borderRadius="xl"
              color="gray.400"
              _hover={{ color: "white", bg: "whiteAlpha.200" }}
              onClick={() => setSelectionReference(undefined)}
            />
          </HStack>
        </Flex>

        {/* Media Library Picker Dropdown/Grid */}
        {showMediaPicker && (
          <Box mb={6} p={4} borderRadius="xl" bg={bgPicker} border="1px dashed" borderColor={borderColor}>
            <Text fontWeight="bold" fontSize="sm" mb={3}>
              Select Ultrasound Scan or Medical Diagram from Course Media:
            </Text>
            <SimpleGrid columns={{ base: 2, md: 4, lg: 6 }} spacing={3} maxH="220px" overflowY="auto">
              {(media || [])
                .filter((m: MediaMeta) => m.type === MediaType.Image)
                .map((m: MediaMeta) => (
                  <Card
                    key={m.id}
                    p={2}
                    cursor="pointer"
                    border="2px solid"
                    borderColor={selectionReference.iconId === m.id ? "teal.400" : "transparent"}
                    _hover={{ borderColor: "cyan.400", transform: "scale(1.02)", shadow: "md" }}
                    transition="all 0.15s"
                    onClick={() => handleSelectExistingMedia(m)}
                  >
                    <Image src={m.fileUrl || m.iconUrl} h="70px" w="100%" objectFit="cover" borderRadius="md" mb={1} />
                    <Text fontSize="2xs" fontWeight="bold" noOfLines={1} textAlign="center">
                      {m.name}
                    </Text>
                  </Card>
                ))}
            </SimpleGrid>
          </Box>
        )}

        {/* Interactive Instructor Framing & Positioning Studio */}
        {isCropping && imageSrc && (
          <Box
            mb={6}
            p={5}
            borderRadius="2xl"
            bg={bgCropper}
            border="1px solid"
            borderColor="cyan.500/30"
            boxShadow="0 4px 20px rgba(6, 182, 212, 0.1)"
          >
            {/* Studio Header */}
            <Flex justify="space-between" align="center" mb={4}>
              <HStack spacing={2.5}>
                <Box
                  p={2}
                  borderRadius="lg"
                  bgGradient="linear(to-br, cyan.500/20, teal.500/20)"
                  color="cyan.400"
                >
                  <FontAwesomeIcon icon={faSliders} />
                </Box>
                <VStack align="start" spacing={0}>
                  <Text fontWeight="bold" fontSize="sm" color={useColorModeValue("gray.800", "gray.100")}>
                    Instructor Framing & Positioning Studio
                  </Text>
                  <Text fontSize="2xs" color="gray.500">
                    Drag the scan or use precision sliders to center exact anatomy. Framing is baked in for mobile.
                  </Text>
                </VStack>
              </HStack>

              <HStack spacing={2}>
                <Select
                  size="xs"
                  w="145px"
                  borderRadius="lg"
                  borderColor={borderColor}
                  value={aspectPreset}
                  onChange={(e) => setAspectPreset(parseFloat(e.target.value))}
                >
                  <option value={360 / 88}>Mobile Card (4.09:1)</option>
                  <option value={16 / 9}>Widescreen (16:9)</option>
                  <option value={21 / 9}>Ultrawide (21:9)</option>
                  <option value={3 / 1}>Banner (3:1)</option>
                </Select>
                <Tooltip label="Center Framing (0, 0)" hasArrow>
                  <IconButton
                    aria-label="Center"
                    size="xs"
                    borderRadius="lg"
                    icon={<FontAwesomeIcon icon={faCrosshairs} />}
                    onClick={() => setCrop({ x: 0, y: 0 })}
                  />
                </Tooltip>
                <Tooltip label="Rotate 90°" hasArrow>
                  <IconButton
                    aria-label="Rotate"
                    size="xs"
                    borderRadius="lg"
                    icon={<FontAwesomeIcon icon={faRotateRight} />}
                    onClick={() => setRotation((r) => (r + 90) % 360)}
                  />
                </Tooltip>
              </HStack>
            </Flex>

            {/* Cropper Viewport */}
            <Box
              position="relative"
              h="300px"
              w="100%"
              bg="#090a10"
              borderRadius="xl"
              overflow="hidden"
              border="1px solid"
              borderColor="cyan.500/30"
              boxShadow="inset 0 2px 10px rgba(0,0,0,0.6)"
            >
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={aspectPreset}
                showGrid={true}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
              <Box
                position="absolute"
                bottom={2}
                left="50%"
                transform="translateX(-50%)"
                pointerEvents="none"
                bg="rgba(10, 12, 16, 0.75)"
                backdropFilter="blur(6px)"
                px={3}
                py={1}
                borderRadius="full"
                border="1px solid"
                borderColor="whiteAlpha.200"
              >
                <Text fontSize="10px" color="gray.300" fontWeight="500">
                  💡 Drag scan directly with mouse or fine-tune with sliders below
                </Text>
              </Box>
            </Box>

            {/* Positioning & Inspector Controls Grid */}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mt={4}>
              {/* Card 1: Precision Positioning (Vertical & Horizontal Shifts) */}
              <Box
                p={4}
                borderRadius="xl"
                bg={bgControlCard}
                border="1px solid"
                borderColor={borderColor}
              >
                <Text fontSize="xs" fontWeight="bold" color="cyan.400" mb={3} textTransform="uppercase" letterSpacing="wider">
                  Positioning Controls (Up / Down & Pan)
                </Text>

                {/* Vertical Shift Slider */}
                <VStack align="stretch" spacing={1.5} mb={3.5}>
                  <Flex justify="space-between" align="center">
                    <HStack spacing={1.5}>
                      <FontAwesomeIcon icon={faArrowUp} style={{ fontSize: "10px", color: "#06b6d4" }} />
                      <FontAwesomeIcon icon={faArrowDown} style={{ fontSize: "10px", color: "#06b6d4" }} />
                      <Text fontSize="xs" fontWeight="600">
                        Vertical Shift (Up / Down):
                      </Text>
                    </HStack>
                    <Badge
                      fontSize="10px"
                      borderRadius="md"
                      px={2}
                      py={0.5}
                      bgGradient={crop.y !== 0 ? "linear(to-r, cyan.500/20, teal.500/20)" : "none"}
                      color={crop.y !== 0 ? "cyan.300" : "gray.400"}
                      border="1px solid"
                      borderColor={crop.y !== 0 ? "cyan.500/40" : borderColor}
                    >
                      {crop.y > 0 ? `+${Math.round(crop.y)}px (Down)` : crop.y < 0 ? `${Math.round(crop.y)}px (Up)` : "0px (Centered)"}
                    </Badge>
                  </Flex>

                  <HStack spacing={2.5}>
                    <Button
                      size="xs"
                      variant="outline"
                      borderRadius="md"
                      px={2}
                      fontSize="10px"
                      onClick={() => setCrop((p) => ({ ...p, y: p.y - 12 }))}
                    >
                      ▲ Up
                    </Button>
                    <Slider
                      min={-200}
                      max={200}
                      step={1}
                      value={crop.y}
                      onChange={(y) => setCrop((p) => ({ ...p, y }))}
                      flex={1}
                    >
                      <SliderTrack bg={useColorModeValue("gray.200", "gray.700")}>
                        <SliderFilledTrack bgGradient="linear(to-r, #06b6d4, #10b981)" />
                      </SliderTrack>
                      <SliderThumb boxSize={3.5} borderColor="cyan.400" />
                    </Slider>
                    <Button
                      size="xs"
                      variant="outline"
                      borderRadius="md"
                      px={2}
                      fontSize="10px"
                      onClick={() => setCrop((p) => ({ ...p, y: p.y + 12 }))}
                    >
                      ▼ Down
                    </Button>
                  </HStack>
                </VStack>

                {/* Horizontal Shift Slider */}
                <VStack align="stretch" spacing={1.5} mb={2}>
                  <Flex justify="space-between" align="center">
                    <HStack spacing={1.5}>
                      <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: "10px", color: "#10b981" }} />
                      <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: "10px", color: "#10b981" }} />
                      <Text fontSize="xs" fontWeight="600">
                        Horizontal Shift (Left / Right):
                      </Text>
                    </HStack>
                    <Badge
                      fontSize="10px"
                      borderRadius="md"
                      px={2}
                      py={0.5}
                      bgGradient={crop.x !== 0 ? "linear(to-r, cyan.500/20, teal.500/20)" : "none"}
                      color={crop.x !== 0 ? "teal.300" : "gray.400"}
                      border="1px solid"
                      borderColor={crop.x !== 0 ? "teal.500/40" : borderColor}
                    >
                      {crop.x > 0 ? `+${Math.round(crop.x)}px (Right)` : crop.x < 0 ? `${Math.round(crop.x)}px (Left)` : "0px (Centered)"}
                    </Badge>
                  </Flex>

                  <HStack spacing={2.5}>
                    <Button
                      size="xs"
                      variant="outline"
                      borderRadius="md"
                      px={2}
                      fontSize="10px"
                      onClick={() => setCrop((p) => ({ ...p, x: p.x - 12 }))}
                    >
                      ◀ Left
                    </Button>
                    <Slider
                      min={-200}
                      max={200}
                      step={1}
                      value={crop.x}
                      onChange={(x) => setCrop((p) => ({ ...p, x }))}
                      flex={1}
                    >
                      <SliderTrack bg={useColorModeValue("gray.200", "gray.700")}>
                        <SliderFilledTrack bgGradient="linear(to-r, #10b981, #06b6d4)" />
                      </SliderTrack>
                      <SliderThumb boxSize={3.5} borderColor="teal.400" />
                    </Slider>
                    <Button
                      size="xs"
                      variant="outline"
                      borderRadius="md"
                      px={2}
                      fontSize="10px"
                      onClick={() => setCrop((p) => ({ ...p, x: p.x + 12 }))}
                    >
                      ▶ Right
                    </Button>
                  </HStack>
                </VStack>

                {/* Quick Centering Button */}
                <Flex justify="flex-end" mt={2}>
                  <Button
                    size="xs"
                    variant="ghost"
                    color="gray.400"
                    _hover={{ color: "cyan.400" }}
                    leftIcon={<FontAwesomeIcon icon={faCrosshairs} />}
                    onClick={() => setCrop({ x: 0, y: 0 })}
                  >
                    Reset & Center (0, 0)
                  </Button>
                </Flex>
              </Box>

              {/* Card 2: Zoom & Quality Optimization */}
              <Box
                p={4}
                borderRadius="xl"
                bg={bgControlCard}
                border="1px solid"
                borderColor={borderColor}
              >
                <Text fontSize="xs" fontWeight="bold" color="teal.400" mb={3} textTransform="uppercase" letterSpacing="wider">
                  Scale & Performance Controls
                </Text>

                {/* Zoom Control */}
                <VStack align="stretch" spacing={1.5} mb={3.5}>
                  <Flex justify="space-between" align="center">
                    <HStack spacing={1.5}>
                      <FontAwesomeIcon icon={faMagnifyingGlassPlus} style={{ fontSize: "11px", color: "#06b6d4" }} />
                      <Text fontSize="xs" fontWeight="600">
                        Zoom Level:
                      </Text>
                    </HStack>
                    <Badge
                      fontSize="10px"
                      borderRadius="md"
                      px={2}
                      py={0.5}
                      color="cyan.300"
                      bg="cyan.500/15"
                      border="1px solid"
                      borderColor="cyan.500/30"
                    >
                      {zoom.toFixed(2)}x
                    </Badge>
                  </Flex>

                  <Slider min={1} max={3} step={0.02} value={zoom} onChange={setZoom}>
                    <SliderTrack bg={useColorModeValue("gray.200", "gray.700")}>
                      <SliderFilledTrack bgGradient="linear(to-r, #06b6d4, #10b981)" />
                    </SliderTrack>
                    <SliderThumb boxSize={3.5} borderColor="cyan.400" />
                  </Slider>

                  {/* Zoom Presets */}
                  <HStack spacing={1.5} pt={1}>
                    <Button size="2xs" variant="outline" borderRadius="md" px={2} onClick={() => setZoom(1)}>
                      1.0x (Fit)
                    </Button>
                    <Button size="2xs" variant="outline" borderRadius="md" px={2} onClick={() => setZoom(1.25)}>
                      1.25x
                    </Button>
                    <Button size="2xs" variant="outline" borderRadius="md" px={2} onClick={() => setZoom(1.5)}>
                      1.5x
                    </Button>
                    <Button size="2xs" variant="outline" borderRadius="md" px={2} onClick={() => setZoom(2.0)}>
                      2.0x
                    </Button>
                  </HStack>
                </VStack>

                {/* Mobile Compression Quality */}
                <VStack align="stretch" spacing={1.5}>
                  <Flex justify="space-between" align="center">
                    <HStack spacing={1.5}>
                      <FontAwesomeIcon icon={faCompress} style={{ fontSize: "11px", color: "#10b981" }} />
                      <Text fontSize="xs" fontWeight="600">
                        Mobile JPEG Compression:
                      </Text>
                    </HStack>
                    <Badge
                      fontSize="10px"
                      borderRadius="md"
                      px={2}
                      py={0.5}
                      color="teal.300"
                      bg="teal.500/15"
                      border="1px solid"
                      borderColor="teal.500/30"
                    >
                      {Math.round(quality * 100)}%
                    </Badge>
                  </Flex>

                  <Slider min={0.4} max={1.0} step={0.05} value={quality} onChange={setQuality}>
                    <SliderTrack bg={useColorModeValue("gray.200", "gray.700")}>
                      <SliderFilledTrack bgGradient="linear(to-r, #10b981, #06b6d4)" />
                    </SliderTrack>
                    <SliderThumb boxSize={3.5} borderColor="teal.400" />
                  </Slider>
                  <Text fontSize="2xs" color="gray.500">
                    Bakes lightweight ~50KB banner image for instant loading on mobile cellular networks.
                  </Text>
                </VStack>
              </Box>
            </SimpleGrid>

            {/* Studio Action Buttons Bar */}
            <Flex justify="space-between" align="center" mt={5} pt={4} borderTop="1px solid" borderColor={borderColor}>
              <HStack spacing={2} color="gray.500">
                <Badge colorScheme="cyan" variant="outline" borderRadius="full" px={2} fontSize="9px">
                  Synced
                </Badge>
                <Text fontSize="2xs">
                  Position and framing will be rendered identically in student iPhones & iPads.
                </Text>
              </HStack>

              <HStack spacing={3}>
                <Button size="sm" variant="ghost" onClick={() => setIsCropping(false)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  bgGradient="linear(to-r, #06b6d4, #10b981)"
                  color="white"
                  fontWeight="600"
                  borderRadius="xl"
                  px={6}
                  leftIcon={<FontAwesomeIcon icon={faCheck} />}
                  isLoading={isUploading}
                  loadingText="Baking & Uploading..."
                  _hover={{
                    bgGradient: "linear(to-r, #0891b2, #059669)",
                    boxShadow: "0 4px 14px rgba(6, 182, 212, 0.4)",
                  }}
                  onClick={handleApplyCropAndUpload}
                >
                  Apply Position & Save Banner
                </Button>
              </HStack>
            </Flex>
          </Box>
        )}

        {/* Live Mobile Card Preview Section (Option A) */}
        <Box mt={2}>
          <Flex justify="space-between" align="center" mb={3}>
            <VStack align="start" spacing={0}>
              <HStack spacing={2}>
                <Text fontWeight="bold" fontSize="sm">
                  Live Mobile App Preview (Option A)
                </Text>
                {activeDisplayImageUrl && (
                  <Badge
                    colorScheme="teal"
                    variant="subtle"
                    borderRadius="full"
                    px={2}
                    fontSize="10px"
                  >
                    Framing Synchronized
                  </Badge>
                )}
              </HStack>
              <Text fontSize="xs" color="gray.500">
                Exact replica of the chapter card as rendered on student iPhones & iPads
              </Text>
            </VStack>

            <HStack spacing={2}>
              {activeDisplayImageUrl && !isCropping && (
                <Button
                  size="xs"
                  bgGradient="linear(to-r, #06b6d4, #10b981)"
                  color="white"
                  borderRadius="full"
                  px={3}
                  leftIcon={<FontAwesomeIcon icon={faArrowsUpDownLeftRight} />}
                  _hover={{
                    bgGradient: "linear(to-r, #0891b2, #059669)",
                    transform: "translateY(-1px)",
                    boxShadow: "0 2px 10px rgba(6, 182, 212, 0.4)",
                  }}
                  onClick={handleStartAdjustCurrentBanner}
                >
                  Adjust Position
                </Button>
              )}
              <HStack spacing={1} p={1} bg={useColorModeValue("gray.100", "gray.800")} borderRadius="full" border="1px solid" borderColor={borderColor}>
                <Button
                  size="xs"
                  borderRadius="full"
                  px={3}
                  variant="solid"
                  bg={previewMode === "dark" ? "linear-gradient(135deg, #06b6d4 0%, #10b981 100%)" : "transparent"}
                  color={previewMode === "dark" ? "white" : useColorModeValue("gray.600", "gray.400")}
                  fontWeight="600"
                  leftIcon={<FontAwesomeIcon icon={faMoon} />}
                  onClick={() => setPreviewMode("dark")}
                  _hover={{ opacity: 0.9 }}
                >
                  Dark Mode
                </Button>
                <Button
                  size="xs"
                  borderRadius="full"
                  px={3}
                  variant="solid"
                  bg={previewMode === "light" ? "linear-gradient(135deg, #06b6d4 0%, #10b981 100%)" : "transparent"}
                  color={previewMode === "light" ? "white" : useColorModeValue("gray.600", "gray.400")}
                  fontWeight="600"
                  leftIcon={<FontAwesomeIcon icon={faSun} />}
                  onClick={() => setPreviewMode("light")}
                  _hover={{ opacity: 0.9 }}
                >
                  Light Mode
                </Button>
              </HStack>
            </HStack>
          </Flex>

          {/* Exact Replica of SubpartCard Option A */}
          <Box
            w="100%"
            maxW="540px"
            mx="auto"
            position="relative"
            borderRadius="16px"
            overflow="hidden"
            minH="92px"
            bg={previewMode === "dark" ? "#15171C" : "#FFFFFF"}
            border="1px solid"
            borderColor={previewMode === "dark" ? "rgba(255, 255, 255, 0.08)" : "#E5E7EB"}
            boxShadow="0 4px 12px rgba(0,0,0,0.15)"
            transition="all 0.2s"
          >
            {/* Background Image Layer */}
            {activeDisplayImageUrl && (
              <Box position="absolute" top={0} left={0} right={0} bottom={0} pointerEvents="none">
                <Image
                  src={activeDisplayImageUrl}
                  w="100%"
                  h="100%"
                  objectFit="cover"
                  position="absolute"
                  top={0}
                  left={0}
                />
                {/* Option A Horizontal Lighting Scrim */}
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  background={
                    previewMode === "dark"
                      ? "linear-gradient(90deg, rgba(13, 14, 18, 0.72) 0%, rgba(13, 14, 18, 0.22) 100%)"
                      : "linear-gradient(90deg, rgba(255, 255, 255, 0.75) 0%, rgba(255, 255, 255, 0.22) 100%)"
                  }
                />
              </Box>
            )}

            {/* Card Content Overlay */}
            <Flex
              position="relative"
              zIndex={2}
              direction="column"
              justify="space-between"
              minH="92px"
              p="14px 18px"
            >
              {/* Header Row: Title & Circular Progress Meter */}
              <Flex justify="space-between" align="center">
                <Text
                  fontSize="16px"
                  fontWeight="700"
                  color={previewMode === "dark" ? "#FFFFFF" : "#0F172A"}
                  noOfLines={2}
                  letterSpacing="-0.2px"
                >
                  {selectionReference.title || "Untitled Chapter"}
                </Text>

                {/* Circular Progress Gauge */}
                <Box
                  w="34px"
                  h="34px"
                  borderRadius="full"
                  border="3px solid"
                  borderColor={previewMode === "dark" ? "rgba(0, 229, 255, 0.3)" : "rgba(0, 229, 255, 0.35)"}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                >
                  <Text fontSize="10px" fontWeight="bold" color="#00e5ff">
                    0%
                  </Text>
                </Box>
              </Flex>

              {/* Media Counts Badge Pill Row */}
              <Flex
                align="center"
                mt="8px"
                pt="6px"
                borderTop="1px solid"
                borderColor={previewMode === "dark" ? "rgba(255,255,255,0.08)" : "#E2E8F0"}
              >
                <HStack
                  spacing={4}
                  bg={
                    activeDisplayImageUrl
                      ? previewMode === "dark"
                        ? "rgba(12, 12, 16, 0.65)"
                        : "rgba(255, 255, 255, 0.75)"
                      : "transparent"
                  }
                  px={activeDisplayImageUrl ? 2.5 : 0}
                  py={1}
                  borderRadius="md"
                >
                  <HStack spacing={1}>
                    <FontAwesomeIcon icon={faFolder} style={{ fontSize: "11px", color: "#00e5ff" }} />
                    <Text fontSize="11px" fontWeight="600" color={previewMode === "dark" ? "#E2E8F0" : "#334155"}>
                      {mediaCounts.section}
                    </Text>
                  </HStack>

                  <HStack spacing={1}>
                    <FontAwesomeIcon icon={faVideo} style={{ fontSize: "11px", color: "#00e5ff" }} />
                    <Text fontSize="11px" fontWeight="600" color={previewMode === "dark" ? "#E2E8F0" : "#334155"}>
                      {mediaCounts.video}
                    </Text>
                  </HStack>

                  <HStack spacing={1}>
                    <FontAwesomeIcon icon={faFileLines} style={{ fontSize: "11px", color: "#00e5ff" }} />
                    <Text fontSize="11px" fontWeight="600" color={previewMode === "dark" ? "#E2E8F0" : "#334155"}>
                      {mediaCounts.pdf}
                    </Text>
                  </HStack>

                  <HStack spacing={1}>
                    <FontAwesomeIcon icon={faVolumeHigh} style={{ fontSize: "11px", color: "#00e5ff" }} />
                    <Text fontSize="11px" fontWeight="600" color={previewMode === "dark" ? "#E2E8F0" : "#334155"}>
                      {mediaCounts.audio}
                    </Text>
                  </HStack>
                </HStack>
              </Flex>
            </Flex>
          </Box>
        </Box>
      </Card>
    </Flex>
  );
}
